// ============================================================
// TMR Portal Admin — o link de assinatura, para entrega manual
// ============================================================
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { CopyableText } from '../../components/CopyableText';
import { Notice } from '../../components/Notice';
import { Skeleton } from '../../components/states';
import { Icons } from '../../lib/icons';
import { useSigningLink } from '../../data/contracts/queries';
import { ContractDialog } from './ContractDialog';

/** Props for {@link SigningLinkDialog}. */
interface SigningLinkDialogProps {
  contractId: string;
  onClose: () => void;
}

/**
 * The link where the Empresa's representative signs, for an operator who needs to hand it over by hand
 * (02 §7).
 *
 * Read from the provider when the dialog opens, never before and never from cache: replacing the signatory
 * invalidates the link, so the only copy worth handing over is the one read just now.
 *
 * The link is shown rather than copied on the click that opened this, for the reason `InviteLinkDialog`
 * gives: a clipboard write that follows an await loses its user activation in some browsers, so the copy
 * hangs off a gesture of its own.
 */
export function SigningLinkDialog({ contractId, onClose }: SigningLinkDialogProps) {
  const link = useSigningLink(contractId);
  const error = link.error instanceof ApiError ? link.error : null;

  return (
    <ContractDialog
      eyebrow="Link de assinatura"
      title="Link para o representante assinar"
      onClose={onClose}
      footer={
        <>
          <Btn variant="secondary" onClick={onClose}>
            Fechar
          </Btn>
          {link.data && (
            <Btn
              variant="primary"
              icon={Icons.copy}
              onClick={() => void navigator.clipboard.writeText(link.data.url)}
            >
              Copiar link
            </Btn>
          )}
        </>
      }
    >
      {link.isPending && <Skeleton rows={2} />}

      {link.isError && (
        <Notice tone="danger">
          {error?.code === 'conflict'
            ? 'Este documento não aceita mais assinatura por este link.'
            : 'Não foi possível ler o link no provedor agora.'}{' '}
          {error?.detail}
        </Notice>
      )}

      {link.data && (
        <>
          <p style={{ margin: '0 0 12px' }}>
            Envie este link direto para o representante legal da empresa, por outro canal.
          </p>
          {/* Wrapped rather than truncated: a cut link that looks whole is worse than an ugly one. */}
          <CopyableText
            value={link.data.url}
            style={{
              fontSize: 12.5,
              fontFamily: 'var(--font-mono)',
              overflowWrap: 'anywhere',
              display: 'inline-block',
            }}
          />
          {link.data.channel === 'WhatsApp' ? (
            <Notice tone="warning">
              O representante confirma a assinatura com um código que chega <strong>no WhatsApp dele</strong>.
              O link sozinho não assina — se o telefone estiver errado, troque o contato em vez de mandar o
              link.
            </Notice>
          ) : (
            <Notice tone="warning">
              O representante confirma a assinatura com um código que chega <strong>no e-mail dele</strong>.
              Mande só para ele, nunca em grupo.
            </Notice>
          )}
        </>
      )}
    </ContractDialog>
  );
}
